enum CommissionStructureStatus {
  INVALID_STATUS
  IN_FORCE
  TERMINATED
  PENDING
}

enum CommissionType {
  INVALID_COMMISSION_TYPE
  COST_PER_LEAD
  REVENUE_SHARE
  TIER
  DIRECT_REVENUE_SHARE
  PROVIDER_SHARE
}

enum CommissionRevenueType {
  INVALID_REVENUE_TYPE
  MORTGAGE_PROC_FEE
  PROTECTION_COMMISSION
  GI_COMMISSION
  CASE_FEES
  REFERRALS
  CONVEYANCING
}

enum CommissionDeductionType {
  INVALID_DEDUCTION_TYPE
  NETWORK_FEE
  MORTGAGE_CLUB_FEE
  CREDIT_SEARCH
  LAND_REGISTRY
  POSTAGE
  INTRODUCER_COMMISSION
  MORTGAGE_REFERENCE
  VALUATION_COST
}

enum CommissionStructureFrequency {
  VALID
  UNDEFINED
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUALLY
  ONCE
}

enum Condition {
  INVALID_COMMISSION_PAID_ON_CONDITION
  IS_ONE_OF_THE_FOLLOWING
  IS_NOT_ONE_OF_THE_FOLLOWING
  CAN_BE_ANY_RELATED_USER
}

enum RelatedUserRelationshipType {
  INVALID_RELATIONSHIP_TYPE
  REFERRER
  ADMINISTRATOR
  LEAD_QUALIFIER
  CASE_MANAGER
  WATCHER
  REVIEWER
}

type CommissionStructure {
  commission_structure_id: ID!
  created_at: String
  modified_at: String
  paying_organisation_id: ID!
  paying_organisation: Organisation
  receiving_organisation_id: ID
  receiving_organisation: Organisation
  regulating_organisation_id: ID
  receiving_user_id: ID
  status: CommissionStructureStatus
  commissions: [Commission!]
  acre_referral_club: Boolean
}

type ReferralPartner {
  case_types: [MortgageReason!]
  commission_structure_id: String
  paying_organisation_id: String
  paying_organisation_name: String
  receiving_organisation_id: String
  receiving_organisation_name: String
  regulating_organisation_id: String
  regulating_organisation_name: String
  users: [MiUser!]
  blacklisted: Boolean
  acre_referral_club: Boolean
}

input GetReferralPartnersInput {
  case_types: [String]
  receiving_organisations: [String]
  regulating_organisation: String
  include_acre_referrals: Boolean
  include_blacklisted: Boolean
  bookmark: String
  page_size: Int
  all_users: Boolean
}

type GetReferralPartnersResponse {
  filters: ReferralPartnerFilters
  next_page: ReferralPartnerNextPage
  referral_partners: [ReferralPartner]
}

type ReferralPartnerFilters {
  case_types: [String]
  receiving_organisations: [String]
  regulating_organisation: String
  bookmark: String
  page_size: Int
  all_users: Boolean
}

type ReferralPartnerNextPage {
  bookmark: String
}

type IntroducerCommission {
  commission_structure_id: ID!
  general_email: String
  joined_acre: String
  name: String
  organisation_id: ID!
  primary_address: String
  primary_phone: String
  status: String
  users: [MiUser]
}

type IntroducerCommissions {
  organisations: [IntroducerCommission]
  next_page: GetFilteredNoteNextPage
}

input IntroducerCommissionsRequestInput {
  org_id: ID
  show_disabled: Boolean
  bookmark: Int
  page_size: Int
  all_users: Boolean
}

input CommissionStructureInput {
  commission_structure_id: ID
  created_at: String
  modified_at: String
  paying_organisation_id: String
  receiving_organisation_id: String
  receiving_user_id: String
  regulating_organisation_id: String
  status: CommissionStructureStatus
  commissions: [CommissionInput!]
}

type IntroducerCondition {
  condition: Condition
  introducer_company: [String]
}

input IntroducerConditionInput {
  condition: Condition
  introducer_company: [String]
}

type RelatedUserCondition {
  condition: Condition
  related_user: [String]
  related_user_relationship_type: RelatedUserRelationshipType
}

input RelatedUserConditionInput {
  condition: Condition
  related_user: [String]
  related_user_relationship_type: RelatedUserRelationshipType
}

input ThresholdInput {
  percentage: Int
  from: String
  to: String
}

input TierCommissionsInput {
  period_id: ID
  all_periods: Boolean
  thresholds: [ThresholdInput]
}

type Threshold {
  percentage: Int
  from: String
  to: String
}

type TierCommissions {
  period_id: ID
  all_periods: Boolean
  thresholds: [Threshold]
}

type Commission {
  internalHash: String
  type: CommissionType
  case_types: [MortgageReason!]
  revenue_types: [CommissionRevenueType!]
  deduction_types: [CommissionDeductionType!]
  fixed_value: String
  percentage: Int
  frequency: CommissionStructureFrequency
  number_of_payments: Int
  payment_due: PaymentDue
  introducer_condition: IntroducerCondition
  related_user_condition: RelatedUserCondition
  user_relation_to_case: [RelatedUserRelationshipType]
  tier_commission: TierCommissions
}

type CommissionColumn {
  id: ID!
  type: CommissionType
  case_types: [MortgageReason!]
  revenue_types: [CommissionRevenueType!]
  deduction_types: [CommissionDeductionType!]
  fixed_value: String
  percentage: Int
  frequency: CommissionStructureFrequency
  number_of_payments: Int
  payment_due: PaymentDue
  introducer_condition: IntroducerCondition
  related_user_condition: RelatedUserCondition
  user_relation_to_case: [RelatedUserRelationshipType]
}

input CommissionInput {
  internalHash: String
  type: CommissionType
  case_types: [MortgageReason!]
  revenue_types: [CommissionRevenueType!]
  deduction_types: [CommissionDeductionType!]
  fixed_value: String
  percentage: Int
  frequency: CommissionStructureFrequency
  number_of_payments: Int
  payment_due: PaymentDue
  introducer_condition: IntroducerConditionInput
  related_user_condition: RelatedUserConditionInput
  user_relation_to_case: [RelatedUserRelationshipType]
  tier_commission: TierCommissionsInput
}

input CommissionStructureRequestInput {
  filter_paying_organisation_id: ID
  commission_structure_ids: [ID!]
  filter_receiving_organisation_id: ID
  filter_receiving_user_id: ID
  filter_regulating_organisation_id: ID
  bookmark: Int
  page_size: Int
}

type CommissionStructureRequest {
  filter_paying_organisation_id: ID
  commission_structure_ids: [ID!]
  filter_receiving_organisation_id: ID
  filter_receiving_user_id: ID
  bookmark: Int
  page_size: Int
}

type CommissionStructureResponse {
  commission_structures: [CommissionStructure!]!
  next_page: CommissionStructureRequest
}

enum PaymentDue {
  INVALID_PAYMENT_DUE
  PRE_RECOMMENDATION
  REVIEW
  POST_RECOMMENDATION_REVIEW
  PRE_APPLICATION
  POST_APPLICATION
  COMPLETE
  NOT_PROCEEDING
  IMPORTING
  IMPORTED_COMPLETE
  LEAD
  APPLICATION_SUBMITTED
  AWAITING_VALUATION
  AWAITING_OFFER
  OFFER_RECEIVED
  EXCHANGE
  COLLECTED
  CREATED
}
